import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import {  Terminal, Monitor, Copy, Check, ShieldAlert } from 'lucide-react';

const DownloadHub = () => {
  const [copied, setCopied] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState('');

  // 1. Fetch User Licenses (To pre-fill command)
  const { data: licenses = [] } = useQuery({
    queryKey: ['myLicenses'],
    queryFn: async () => {
      const res = await axiosClient.get('/account/licenses');
      const active = res.data?.filter(l => !l.revoked) || [];
      if (active.length > 0) setSelectedLicense(active[0].license_key);
      return active;
    }
  });

  // 2. Fetch Download Links (From Admin Settings)
  const { data: links } = useQuery({
    queryKey: ['agentLinks'],
    queryFn: async () => {
      const res = await axiosClient.get('/installer/links');
      return res.data || {};
    }
  });

  const installCommand = `curl -sL ${links?.linux || 'https://...'} | sudo bash -s -- --license ${selectedLicense || 'YOUR_LICENSE_KEY'}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(installCommand);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8 animate-fade-in pb-10">
      
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-8 rounded-2xl text-white shadow-xl">
        <h1 className="text-3xl font-bold mb-2">Download Agent</h1>
        <p className="text-slate-400">Select your platform to install the autonomous sentinel.</p>
        
        {licenses.length === 0 ? (
            <div className="mt-6 bg-red-500/10 border border-red-500/20 p-4 rounded-xl flex items-center text-red-200">
                <ShieldAlert className="w-5 h-5 mr-3" />
                <span>You need an active license to install the agent. Please purchase a plan first.</span>
            </div>
        ) : (
            <div className="mt-6">
                <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Select License for Installation</label>
                <select 
                    className="bg-slate-900/50 border border-slate-600 text-white p-2 rounded-lg text-sm font-mono w-full max-w-md outline-none focus:border-blue-500"
                    value={selectedLicense}
                    onChange={(e) => setSelectedLicense(e.target.value)}
                >
                    {licenses.map(l => (
                        <option key={l.license_key} value={l.license_key}>
                            {l.package_name} - {l.license_key}
                        </option>
                    ))}
                </select>
            </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* LINUX CARD (Special Command Logic) */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all col-span-1 md:col-span-2 lg:col-span-2">
            <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center mr-3"><Terminal className="w-6 h-6"/></div>
                <div>
                    <h3 className="font-bold text-lg text-slate-800">Linux Server</h3>
                    <p className="text-xs text-slate-500">Ubuntu, Debian, CentOS, RHEL</p>
                </div>
            </div>
            
            <div className="bg-slate-900 rounded-xl p-4 font-mono text-sm text-green-400 relative group">
                <div className="overflow-x-auto pb-2">{installCommand}</div>
                <button 
                    onClick={copyToClipboard} 
                    className="absolute top-3 right-3 p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                    title="Copy Command"
                >
                    {copied ? <Check className="w-4 h-4 text-green-400"/> : <Copy className="w-4 h-4"/>}
                </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-2 flex items-center">
                <ShieldAlert className="w-3 h-3 mr-1"/> Run this command as root (sudo). Requires 'curl'.
            </p>
        </div>

        {/* WINDOWS CARD */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all opacity-60">
            <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mr-3"><Monitor className="w-6 h-6"/></div>
                <div><h3 className="font-bold text-lg text-slate-800">Windows</h3><p className="text-xs text-slate-500">Coming Soon</p></div>
            </div>
            <button disabled className="w-full border border-dashed border-slate-300 text-slate-400 py-2 rounded-lg text-sm cursor-not-allowed">Unavailable</button>
        </div>

      </div>
    </div>
  );
};

export default DownloadHub;